import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import AnimatedKpiCard from '../../components/common/AnimatedKpiCard';
import ChartCard from '../../components/charts/ChartCard';
import { LineChart, BarChart, DoughnutChart, EmptyChart } from '../../components/charts/AnalyticsCharts';
import { AnalyticsToolbar, AnalyticsSettingsModal, AnalyticsInfoModal, useAnalyticsControls } from './AnalyticsControls';

const fmtINR = (n) => new Intl.NumberFormat('en-IN', {
  style: 'currency', currency: 'INR', maximumFractionDigits: 0,
}).format(Number(n) || 0);
const ANALYTICS_PRESETS = [
  { value: 'today', label: 'Today vs Yesterday' },
  { value: 'week', label: 'This Week vs Last Week' },
  { value: 'month', label: 'This Month vs Last Month' },
  { value: 'year', label: 'This Year vs Last Year' },
];

const formatDate = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const startOfDay = (value) => {
  const d = new Date(value);
  d.setHours(0, 0, 0, 0);
  return d;
};

const endOfDay = (value) => {
  const d = new Date(value);
  d.setHours(23, 59, 59, 999);
  return d;
};

const shiftDate = (value, amount, unit) => {
  const d = new Date(value);
  if (unit === 'month') d.setMonth(d.getMonth() + amount);
  else if (unit === 'year') d.setFullYear(d.getFullYear() + amount);
  else d.setDate(d.getDate() + amount);
  return d;
};

const buildPreviousQueryParams = (queryParams = {}) => {
  const preset = String(queryParams.preset || '12m');
  const groupBy = queryParams.groupBy || 'month';

  if (preset === 'today') {
    const today = startOfDay(new Date());
    const previous = shiftDate(today, -1, 'day');
    return { preset: 'custom', groupBy, from: formatDate(previous), to: formatDate(previous) };
  }

  if (preset === 'week') {
    const today = endOfDay(new Date());
    const currentStart = startOfDay(today);
    currentStart.setDate(currentStart.getDate() - currentStart.getDay());
    return { preset: 'custom', groupBy, from: formatDate(shiftDate(currentStart, -7, 'day')), to: formatDate(shiftDate(today, -7, 'day')) };
  }

  if (preset === '7d' || preset === '30d') {
    const days = preset === '7d' ? 7 : 30;
    const currentEnd = endOfDay(new Date());
    const currentStart = startOfDay(shiftDate(currentEnd, -(days - 1), 'day'));
    const previousEnd = endOfDay(shiftDate(currentStart, -1, 'day'));
    const previousStart = startOfDay(shiftDate(previousEnd, -(days - 1), 'day'));
    return { preset: 'custom', groupBy, from: formatDate(previousStart), to: formatDate(previousEnd) };
  }

  if (preset === 'month') {
    const now = new Date();
    const prevMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const prevMonthEnd = new Date(now.getFullYear(), now.getMonth() - 1, Math.min(now.getDate(), new Date(now.getFullYear(), now.getMonth(), 0).getDate()));
    return { preset: 'custom', groupBy, from: formatDate(prevMonthStart), to: formatDate(prevMonthEnd) };
  }

  if (preset === 'year') {
    const now = new Date();
    return {
      preset: 'custom',
      groupBy,
      from: `${now.getFullYear() - 1}-01-01`,
      to: formatDate(new Date(now.getFullYear() - 1, now.getMonth(), Math.min(now.getDate(), new Date(now.getFullYear() - 1, now.getMonth() + 1, 0).getDate()))),
    };
  }

  if (preset === '12m') {
    const now = new Date();
    const currentStart = new Date(now.getFullYear(), now.getMonth() - 11, 1);
    const previousStart = new Date(currentStart.getFullYear(), currentStart.getMonth() - 12, 1);
    const previousEnd = new Date(currentStart.getFullYear(), currentStart.getMonth(), 0);
    return { preset: 'custom', groupBy, from: formatDate(previousStart), to: formatDate(previousEnd) };
  }

  if (queryParams.from || queryParams.to) {
    const currentFrom = startOfDay(queryParams.from || queryParams.to || new Date());
    const currentTo = endOfDay(queryParams.to || queryParams.from || new Date());
    const diffMs = currentTo.getTime() - currentFrom.getTime();
    const previousTo = endOfDay(shiftDate(currentFrom, -1, 'day'));
    const previousFrom = startOfDay(new Date(previousTo.getTime() - diffMs));
    return { preset: 'custom', groupBy, from: formatDate(previousFrom), to: formatDate(previousTo) };
  }

  return { preset: '30d', groupBy };
};

const buildComparisonMeta = (currentMeta = {}, previousMeta = {}) => ({
  presentLabel: currentMeta.periodLabel || 'Present period',
  previousLabel: previousMeta.periodLabel || 'Previous period',
});

export default function EnrollmentAnalyticsV2() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [previousData, setPreviousData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [allCourses, setAllCourses] = useState([]);
  const [showCoursePicker, setShowCoursePicker] = useState(false);
  const coursePickerRef = useRef(null);
  const [selectedCourses, setSelectedCourses] = useState([]);
  useEffect(() => {
    if (!showCoursePicker) return undefined;
    const outside = (event) => {
      if (!coursePickerRef.current?.contains(event.target)) setShowCoursePicker(false);
    };
    const escape = (event) => { if (event.key === 'Escape') setShowCoursePicker(false); };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [showCoursePicker]);
  const controls = useAnalyticsControls({
    storageKey: 'enrollments',
    chartKeys: ['growth', 'payment', 'courses', 'revenue', 'courseTrend', 'sourceTrend'],
    defaultPreset: 'month',
    defaultGroupBy: 'day',
  });

  const handlePeriodChange = (preset) => {
    controls.setPreset(preset);
    controls.setGroupBy(preset === 'year' ? 'month' : 'day');
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const previousParams = buildPreviousQueryParams(controls.queryParams);
      const [currentRes, previousRes] = await Promise.all([
        api.get('/training/analytics/enrollment', { params: controls.queryParams }),
        api.get('/training/analytics/enrollment', { params: previousParams }),
      ]);
      setData(currentRes.data?.data || {});
      setPreviousData(previousRes.data?.data || {});
    } catch (_) {
      setData({});
      setPreviousData({});
    } finally {
      setLoading(false);
    }
  }, [controls.queryParams]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/training/courses', { params: { status: 'active', limit: 500 } })
      .then((r) => {
        const rows = Array.isArray(r.data?.data) ? r.data.data : Array.isArray(r.data?.records) ? r.data.records : [];
        setAllCourses(rows);
      })
      .catch(() => setAllCourses([]));
  }, []);

  const currentData = data || {};
  const previousAnalytics = previousData || {};
  const kpis = currentData.kpis || {};
  const trend = currentData.monthlyTrend || [];
  const slices = currentData.paymentSlices || [];
  const courses = useMemo(() => currentData.courseDistribution || [], [currentData.courseDistribution]);
  const previousCourses = useMemo(() => previousAnalytics.courseDistribution || [], [previousAnalytics.courseDistribution]);
  const courseSeries = useMemo(() => currentData.courseTrendSeries || [], [currentData.courseTrendSeries]);
  const previousCourseSeries = useMemo(() => previousAnalytics.courseTrendSeries || [], [previousAnalytics.courseTrendSeries]);
  const sourceTrend = currentData.sourceTrend || [];
  const meta = currentData.meta || {};
  const previousMeta = previousAnalytics.meta || {};
  const visible = controls.state.chartVisibility || {};
  const comparisonMeta = buildComparisonMeta(meta, previousMeta);
  const chartOptions = [
    { key: 'growth', label: 'Enrollment Overview', help: 'Compare selected and previous period totals' },
    { key: 'payment', label: 'Payment Status', help: 'Paid, partial, pending, overdue' },
    { key: 'courses', label: 'Course Distribution', help: 'Top courses by student count' },
    { key: 'revenue', label: 'Revenue Trend', help: 'Collected fee trend' },
    { key: 'courseTrend', label: 'Course Line Comparison', help: 'Toggle individual course lines' },
    { key: 'sourceTrend', label: 'LMS vs Manual', help: 'Package-driven enrollments compared with manual intake' },
  ];

  const courseOptions = useMemo(() => {
    const map = new Map();
    allCourses.forEach((item) => {
      const key = String(item._id || item.courseId || '');
      if (!key) return;
      if (!map.has(key)) map.set(key, { value: key, label: item.courseName || item.title || 'Course' });
    });
    [...courseSeries, ...previousCourseSeries].forEach((item) => {
      const key = String(item.courseId || item.courseName || '');
      if (!key || map.has(key)) return;
      map.set(key, { value: key, label: item.courseName || 'Course' });
    });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [allCourses, courseSeries, previousCourseSeries]);

  const activeCourseIds = useMemo(() => {
    const ids = [
      ...courseSeries.filter((item) => Number(item.total || 0) > 0).map((item) => String(item.courseId || item.courseName || '')),
      ...previousCourseSeries.filter((item) => Number(item.total || 0) > 0).map((item) => String(item.courseId || item.courseName || '')),
    ].filter(Boolean);
    return Array.from(new Set(ids));
  }, [courseSeries, previousCourseSeries]);

  useEffect(() => {
    setSelectedCourses((current) => {
      const valid = current.filter((value) => activeCourseIds.includes(value));
      return valid.length === current.length ? current : valid;
    });
  }, [activeCourseIds]);

  const comparisonCourseOptions = courseOptions.filter((option) => activeCourseIds.includes(option.value));

  const selectedComparison = useMemo(() => {
    const currentMap = new Map(courseSeries.map((item) => [String(item.courseId || item.courseName || ''), item]));
    const previousMap = new Map(previousCourseSeries.map((item) => [String(item.courseId || item.courseName || ''), item]));
    const optionMap = new Map(courseOptions.map((option) => [option.value, option]));
    return (selectedCourses.length ? selectedCourses : activeCourseIds).map((value) => {
      const present = currentMap.get(value);
      const previous = previousMap.get(value);
      const option = optionMap.get(value);
      return {
        key: value,
        courseName: option?.label || present?.courseName || previous?.courseName || 'Course',
        present: Number(present?.total || 0),
        previous: Number(previous?.total || 0),
      };
    }).filter((item) => item.courseName);
  }, [selectedCourses, activeCourseIds, courseSeries, previousCourseSeries, courseOptions]);

  const presentLabel = comparisonMeta.presentLabel;
  const previousLabel = comparisonMeta.previousLabel;
  const chartComparison = selectedComparison;
  const previousTrend = previousAnalytics.monthlyTrend || [];
  const presentTotal = Number(kpis.total || 0);
  const previousTotal = Number(previousAnalytics.kpis?.total || 0);
  const totalDifference = presentTotal - previousTotal;
  const totalDifferencePercent = previousTotal > 0 ? Math.round(totalDifference / previousTotal * 100) : null;
  const overviewLabels = (trend.length >= previousTrend.length ? trend : previousTrend).map((item) => item.label);
  const overviewDatasets = [
    { label: 'Present period', data: trend.map((item) => Number(item.count || 0)), color: '#2563eb', fill: true, pointRadius: 3 },
    { label: 'Previous equivalent period', data: previousTrend.map((item) => Number(item.count || 0)), color: '#f97316', fill: false, pointRadius: 3 },
  ];
  const buildCourseChart = (seriesList) => {
    const seriesMap = new Map(seriesList.map((series) => [String(series.courseId || series.courseName || ''), series]));
    const series = chartComparison.map((item) => ({ ...seriesMap.get(item.key), courseName: item.courseName })).filter((item) => item.points?.length);
    return {
      labels: (series[0]?.points || []).map((point) => point.label),
      datasets: series.map((item) => ({ label: item.courseName, data: item.points.map((point) => Number(point.value || 0)), color: item.color, fill: false, pointRadius: item.points.length === 1 ? 6 : 3 })),
    };
  };
  const presentCourseChart = buildCourseChart(courseSeries);
  const previousCourseChart = buildCourseChart(previousCourseSeries);

  if (loading && !data) return <LoadingSpinner />;
  if (!data) return null;

  return (
    <div className="enrollment-analytics">
      <AnalyticsToolbar
        title="Enrollment Comparison"
        subtitle="Choose a quick period or use From and To for a custom range."
        state={controls.state}
        meta={meta}
        presets={ANALYTICS_PRESETS}
        showTitle
        showGroupBy={false}
        dateInputLabels
        settingsLabel="Show / Hide Charts"
        infoLabel="How It Works"
        wrapPresets
        onPresetChange={handlePeriodChange}
        onGroupByChange={controls.setGroupBy}
        onFromChange={controls.setFrom}
        onToChange={controls.setTo}
        onOpenSettings={() => setShowSettings(true)}
        onOpenInfo={() => setShowInfo(true)}
      />

      {visible.growth !== false && <div className="row g-3 mb-4"><div className="col-12">
        <ChartCard title="Enrollments: Present vs Previous" subtitle={`${presentLabel} compared with ${previousLabel}`} icon="bi bi-arrow-left-right" iconColor="#2563eb" onClick={() => navigate('/enrollments')}>
          <div className="row g-2 mb-3">
            <div className="col-12 col-md-4"><div className="border rounded-3 px-3 py-2 h-100" style={{ background: 'rgba(37,99,235,0.06)' }}><div className="small text-muted">Selected period</div><div className="fs-4 fw-bold text-primary">{presentTotal}</div><div className="small text-muted text-truncate" title={presentLabel}>{presentLabel}</div></div></div>
            <div className="col-12 col-md-4"><div className="border rounded-3 px-3 py-2 h-100" style={{ background: 'rgba(148,163,184,0.08)' }}><div className="small text-muted">Previous period</div><div className="fs-4 fw-bold text-dark">{previousTotal}</div><div className="small text-muted text-truncate" title={previousLabel}>{previousLabel}</div></div></div>
            <div className="col-12 col-md-4"><div className="border rounded-3 px-3 py-2 h-100" style={{ background: totalDifference >= 0 ? 'rgba(34,197,94,0.07)' : 'rgba(239,68,68,0.07)' }}><div className="small text-muted">Change</div><div className={`fs-4 fw-bold ${totalDifference >= 0 ? 'text-success' : 'text-danger'}`}>{totalDifference >= 0 ? '+' : ''}{totalDifference}</div><div className={`small ${totalDifference >= 0 ? 'text-success' : 'text-danger'}`}>{totalDifferencePercent === null ? (presentTotal > 0 ? 'No enrollments in the previous period' : 'No change') : `${totalDifference >= 0 ? 'Up' : 'Down'} ${Math.abs(totalDifferencePercent)}% from previous`}</div></div></div>
          </div>
          {trend.length <= 1 && previousTrend.length <= 1
            ? <BarChart height={280} labels={['Daily enrollments']} datasets={[{ label: presentLabel, data: [presentTotal], color: '#2563eb' }, { label: previousLabel, data: [previousTotal], color: '#f97316' }]} themeColor="#2563eb" />
            : <LineChart height={280} labels={overviewLabels} datasets={overviewDatasets} themeColor="#2563eb" yMin={0} xAxisTitle="Selected-period dates" yAxisTitle="Enrollments" />}
        </ChartCard>
      </div></div>}

      <div className="row g-3 mb-4">
        {visible.courseTrend !== false && (
          <div className="col-12">
            <ChartCard title="Course Performance Comparison" subtitle={`${presentLabel} compared with ${previousLabel}`} icon="bi bi-bar-chart-line-fill" iconColor="#f59e0b" onClick={() => navigate('/enrollments')}>
              <div className="d-flex flex-column gap-3">
                <div className="d-flex flex-column flex-lg-row align-items-start justify-content-between gap-3">
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    <span className="badge text-bg-primary">{presentLabel}</span>
                    <span className="badge text-bg-light border">{previousLabel}</span>
                  </div>
                  <div className="w-100 position-relative" ref={coursePickerRef} onClick={(event) => event.stopPropagation()}>
                    <label className="form-label form-label-sm mb-2 fw-semibold">Filter courses (optional)</label>
                    <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-between" style={{ width: '100%', maxWidth: 320, minWidth: 0 }} onClick={() => setShowCoursePicker((current) => !current)}>
                      <span>{selectedCourses.length ? `${selectedCourses.length} course${selectedCourses.length > 1 ? 's' : ''} selected` : 'All courses with enrollments'}</span>
                      <i className={`bi ${showCoursePicker ? 'bi-chevron-up' : 'bi-chevron-down'}`} />
                    </button>
                    {showCoursePicker && (
                      <div className="position-absolute mt-2 p-3 border rounded-3 bg-white shadow-sm" style={{ zIndex: 20, width: 'min(100%, 320px)', maxHeight: 280, overflowY: 'auto' }}>
                        <div className="d-flex flex-wrap gap-2 mb-3">
                          <button type="button" className="btn btn-light btn-sm border" onClick={() => { setSelectedCourses(comparisonCourseOptions.slice(0, 6).map((option) => option.value)); setShowCoursePicker(false); }}>Top 6</button>
                          <button type="button" className="btn btn-light btn-sm border" onClick={() => { setSelectedCourses([]); setShowCoursePicker(false); }}>Show all</button>
                        </div>
                        <div className="d-flex flex-column gap-2">
                          {comparisonCourseOptions.map((option) => (
                            <label key={option.value} className="d-flex align-items-center gap-2 small">
                              <input type="checkbox" className="form-check-input mt-0" checked={selectedCourses.includes(option.value)} onChange={(e) => {
                                setSelectedCourses((current) => e.target.checked ? [...current, option.value] : current.filter((value) => value !== option.value));
                                setShowCoursePicker(false);
                              }} />
                              <span>{option.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="d-flex flex-wrap gap-2 mt-2">
                      {selectedCourses.map((value) => {
                        const option = courseOptions.find((entry) => entry.value === value);
                        return option ? <span key={value} className="badge rounded-pill text-bg-light border text-dark">{option.label}</span> : null;
                      })}
                    </div>
                  </div>
                </div>
                {selectedComparison.length ? (
                  <>
                    <div className="row g-3">
                      <div className="col-12">
                        <ChartCard title="Present Trend" subtitle={presentLabel} icon="bi bi-graph-up-arrow" iconColor="#3b82f6" onClick={() => navigate('/enrollments')}>
                          <LineChart height={260} labels={presentCourseChart.labels} datasets={presentCourseChart.datasets} themeColor="#3b82f6" yMin={0} />
                        </ChartCard>
                      </div>
                      <div className="col-12">
                        <ChartCard title="Previous Trend" subtitle={previousLabel} icon="bi bi-graph-up" iconColor="#94a3b8" onClick={() => navigate('/enrollments')}>
                          <LineChart height={260} labels={previousCourseChart.labels} datasets={previousCourseChart.datasets} themeColor="#94a3b8" yMin={0} />
                        </ChartCard>
                      </div>
                    </div>
                    <div className="row g-2">
                      {selectedComparison.map((item) => <div className="col-12 col-sm-6 col-xl-4" key={item.key}>
                        <div className="border rounded-3 px-3 py-2 h-100">
                          <div className="fw-semibold text-truncate" title={item.courseName}>{item.courseName}</div>
                          <div className="d-flex align-items-center justify-content-between gap-2 mt-2 small"><span className="text-primary">Present <strong>{item.present}</strong></span><span style={{ color: '#ea580c' }}>Previous <strong>{item.previous}</strong></span></div>
                          <div className={`small mt-1 ${item.present >= item.previous ? 'text-success' : 'text-danger'}`}>Change {item.present > item.previous ? '+' : ''}{item.present - item.previous}</div>
                        </div>
                      </div>)}
                    </div>
                  </>
                ) : <EmptyChart label="course comparison" themeColor="#6366f1" />}
              </div>
            </ChartCard>
          </div>
        )}
      </div>

      <div className="row g-3 mb-4">
        <div className="col-6 col-md-4 col-xl-3"><AnimatedKpiCard icon="bi-people-fill" label="Total Enrollments" value={kpis.total} loading={loading} color="#3b82f6" sub="selected period" /></div>
        <div className="col-6 col-md-4 col-xl-3"><AnimatedKpiCard icon="bi-person-plus-fill" label="New in Range" value={kpis.newThisMonth} loading={loading} color="#22c55e" sub="current filter" /></div>
        <div className="col-6 col-md-4 col-xl-3"><AnimatedKpiCard icon="bi-collection-play-fill" label="LMS / Package" value={kpis.lmsEnrollments} loading={loading} color="#0ea5e9" sub="membership + promotion" /></div>
        <div className="col-6 col-md-4 col-xl-3"><AnimatedKpiCard icon="bi-pencil-square" label="Manual / Direct" value={kpis.manualEnrollments} loading={loading} color="#64748b" sub="manual, admin, import" /></div>
        <div className="col-6 col-md-4 col-xl-3"><AnimatedKpiCard icon="bi-mortarboard-fill" label="Active Students" value={kpis.activeStudents} loading={loading} color="#06b6d4" sub="selected period" /></div>
        <div className="col-6 col-md-4 col-xl-3"><AnimatedKpiCard icon="bi-patch-check-fill" label="Completed" value={kpis.completed} loading={loading} color="#8b5cf6" sub="completed enrollments" /></div>
        <div className="col-6 col-md-4 col-xl-3"><AnimatedKpiCard icon="bi-exclamation-circle-fill" label="Pending Payments" value={kpis.pendingPayments} loading={loading} color="#f59e0b" sub="need follow-up" /></div>
        <div className="col-6 col-md-4 col-xl-3"><AnimatedKpiCard icon="bi-currency-rupee" label="Total Revenue" value={fmtINR(kpis.totalRevenue)} loading={loading} color="#10b981" sub="selected period" /></div>
      </div>

      <div className="row g-3 mb-4">
        {visible.payment !== false && <div className="col-lg-4"><ChartCard title="Payment Status" subtitle={meta.periodLabel || 'Distribution across filtered enrollments'} icon="bi-pie-chart-fill" iconColor="#22c55e" onClick={() => navigate('/enrollments?paymentStatus=pending')}>{slices.length ? <DoughnutChart height={280} slices={slices} centerLabel={slices.reduce((s, x) => s + x.value, 0)} centerSubLabel="Total" themeColor="#22c55e" /> : <EmptyChart label="payment status" themeColor="#22c55e" />}</ChartCard></div>}
      </div>

      <div className="row g-3 mb-4">
        {visible.sourceTrend !== false && (
          <div className="col-12">
            <ChartCard
              title="LMS vs Manual Intake"
              subtitle={meta.periodLabel || 'Package-led enrollments compared with direct/manual intake'}
              icon="bi-diagram-3-fill"
              iconColor="#0ea5e9"
              onClick={() => navigate('/enrollments?source=lms')}
            >
              {sourceTrend.length ? (
                <LineChart
                  height={280}
                  labels={sourceTrend.map((row) => row.label)}
                  datasets={[
                    { label: 'LMS / Package', data: sourceTrend.map((row) => row.lms), color: '#0ea5e9', fill: false, pointRadius: 4 },
                    { label: 'Manual / Direct', data: sourceTrend.map((row) => row.manual), color: '#64748b', fill: false, pointRadius: 4 },
                  ]}
                  yMin={0}
                />
              ) : <EmptyChart label="source trend" themeColor="#0ea5e9" />}
            </ChartCard>
          </div>
        )}
      </div>

      <div className="row g-3 mb-4">
        {visible.courses !== false && <div className="col-lg-7"><ChartCard title="Course-wise Enrollment" subtitle={meta.periodLabel || 'Top courses by student count'} icon="bi-bar-chart-line-fill" iconColor="#8b5cf6" onClick={() => navigate('/enrollments')}>{courses.length ? <BarChart height={Math.max(180, courses.length * 32)} labels={courses.map((c) => (c.courseName || '').length > 18 ? `${c.courseName.slice(0, 17)}...` : c.courseName)} datasets={[{ label: 'Students', data: courses.map((c) => c.count), color: '#8b5cf6' }]} themeColor="#8b5cf6" onBarClick={({ index }) => { const course = courses[index]; if (course) navigate(`/enrollments?course=${encodeURIComponent(course.courseId)}`); }} /> : <EmptyChart label="courses" themeColor="#8b5cf6" />}</ChartCard></div>}
        {visible.revenue !== false && <div className="col-lg-5"><ChartCard title="Revenue Trend" subtitle={meta.periodLabel || 'Collected fees'} icon="bi-currency-rupee" iconColor="#10b981" onClick={() => navigate('/enrollments?paymentStatus=completed')}>{trend.length ? <BarChart height={280} labels={trend.map((m) => m.label)} datasets={[{ label: 'Revenue', data: trend.map((m) => m.revenue), color: '#10b981' }]} themeColor="#10b981" onBarClick={({ index }) => navigate(`/enrollments?month=${encodeURIComponent(trend[index].key)}`)} /> : <EmptyChart label="revenue" themeColor="#10b981" />}</ChartCard></div>}
      </div>
      <AnalyticsSettingsModal show={showSettings} charts={chartOptions} visibility={visible} onToggle={controls.toggleChart} onReset={controls.reset} onClose={() => setShowSettings(false)} />
      <AnalyticsInfoModal show={showInfo} title="Enrollment Analytics Guide" lines={['Pick weekly, monthly, or yearly grouping based on the review period.', 'Custom dates let admin compare exact collection windows.', 'Chart visibility is stored locally for this analytics view.']} onClose={() => setShowInfo(false)} />
    </div>
  );
}
